import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { RunConfirm } from '@/types/api';

/**
 * Hooks für die Run-Engine (Sprint 5, WT-502/WT-505/WT-506/WT-507).
 *
 * Backend-Endpunkte (siehe app/api/routes/runs.py):
 *   GET    /runs?customer_id=&status=&limit=  -> RunListResponse
 *   POST   /runs                              -> RunCreateResponse
 *   GET    /runs/{id}                         -> RunDetailResponse (inkl. steps)
 *   GET    /runs/{id}/steps                   -> RunStepsResponse
 *   GET    /runs/{id}/logs?since_seq=&limit=  -> RunLogsResponse
 *   POST   /runs/{id}/abort                   -> Run
 *   POST   /runs/{id}/rollback                -> RunRollbackResponse
 *   POST   /runs/batch                        -> BatchRunsResponse (Komplettwartung)
 *   GET    /customers/{id}/maintenance-preview -> CustomerMaintenancePreview
 *   WS     /runs/{id}/stream                  -> Live-Logs (siehe useRunStream)
 *
 * Polling-Strategie:
 *  - useRun: refetchInterval solange status nicht terminal ist
 *  - useRuns: 5s Polling solange irgendein Run im Set "running" ist
 */

export type RunStatus =
  | 'queued'
  | 'preparing'
  | 'running'
  | 'success'
  | 'partial'
  | 'failed'
  | 'blocked'
  | 'aborted'
  | 'rolled_back';

export type RunMode = 'apply' | 'check' | 'rollback';

// 'blocked' ist terminal: der Lauf ist zu Ende, er hat nur nie stattgefunden
// (Vorfall 13.08.2026). Ohne diesen Eintrag pollt das Run-Detail einen
// blockierten Lauf bis in alle Ewigkeit weiter und zeigt ihn als "laeuft".
export const TERMINAL_RUN_STATUSES: RunStatus[] = [
  'success',
  'partial',
  'failed',
  'blocked',
  'aborted',
  'rolled_back',
];

/** Nicht-terminale Backend-Status: Live-Polling läuft solange einer davon aktiv ist. */
export const ACTIVE_RUN_STATUSES: RunStatus[] = ['queued', 'preparing', 'running'];

export function isTerminal(status: string | undefined): boolean {
  if (!status) return false;
  return (TERMINAL_RUN_STATUSES as string[]).includes(status);
}

export function isActive(status: string | undefined): boolean {
  if (!status) return false;
  return (ACTIVE_RUN_STATUSES as string[]).includes(status);
}

export interface Run {
  id: string;
  customer_id: string;
  triggered_by: string;
  /** mode entspricht RunMode, aber Backend liefert string. */
  mode: string;
  /** status entspricht RunStatus, aber Backend liefert string. */
  status: string;
  started_at?: string | null;
  finished_at?: string | null;
  duration_seconds?: number | null;
  target_system_ids: string[];
  routine_ids: string[];
  aborted_reason?: string | null;
  idempotency_key?: string | null;
  created_at: string;
  /**
   * SHA256-Hex (64) der wt-client.py, die diesen Lauf abgeholt hat - vom
   * Client GEMELDET, nicht vom Server nachgemessen (ADR-011: der Server sieht
   * nur, was der Client ihm sagt). null = nichts gemeldet.
   */
  client_version?: string | null;
  /**
   * true  = die gemeldete Fassung weicht von der ab, die der Server unter
   *         /client/download ausliefert -> der Lauf lief mit altem Code.
   * false = beide identisch.
   * null  = UNBEKANNT. Ausdrücklich NICHT dasselbe wie false: entweder hat
   *         der Client nichts gemeldet oder der Server kann seine eigene
   *         ausgelieferte Fassung nicht bestimmen. Am 13.08.2026 lief Colins
   *         Client einen Tag lang mit altem Code, ohne dass irgendwo etwas
   *         stand - ein grüner Haken auf einer ungeprüften Annahme wäre
   *         genau derselbe Fehler mit besserem Gewissen.
   */
  client_outdated?: boolean | null;
  /**
   * Begründeter Snapshot-Verzicht (Befund 20.08.2026, Migration 0029).
   * null/fehlend = es wurde nicht verzichtet. Gesetzt heißt: dieser Lauf
   * fuhr BEWUSST ohne Pre-Snapshot, und hier steht, warum, worauf sich der
   * Verzicht stützt, wann und durch wen - die UI muss das sichtbar machen,
   * sonst ist das Feld nur eine bessere Datenbankspalte.
   */
  snapshot_waiver_reason?: string | null;
  snapshot_waiver_evidence?: string | null;
  snapshot_waiver_at?: string | null;
  snapshot_waiver_actor?: string | null;
}

export interface RunStep {
  id: string;
  run_id: string;
  system_id?: string | null;
  routine_id?: string | null;
  sequence: number;
  name: string;
  /**
   * RunStep-Status: pending|running|success|failed|skipped (Backend-string).
   * ADR-011 Etappe 2 (Client-Runs) ergänzt: unreachable (Host down, rc=4,
   * UI wie failed) und changed (erfolgreich MIT Änderungen, UI wie success).
   */
  status: string;
  started_at?: string | null;
  finished_at?: string | null;
  duration_seconds?: number | null;
  exit_code?: number | null;
  error_message?: string | null;
  stdout_excerpt?: string | null;
}

export interface RunDetail extends Run {
  steps: RunStep[];
}

export interface RunListResponse {
  data: Run[];
  total: number;
}

export interface RunStepsResponse {
  run_id: string;
  data: RunStep[];
  total: number;
}

export const runKeys = {
  all: ['runs'] as const,
  list: (filter: RunsFilter) => ['runs', 'list', filter] as const,
  detail: (id: string) => ['runs', 'detail', id] as const,
  steps: (id: string) => ['runs', 'detail', id, 'steps'] as const,
  logs: (id: string) => ['runs', 'detail', id, 'logs'] as const,
  maintenancePreview: (customerId: string) =>
    ['runs', 'maintenance-preview', customerId] as const,
};

export interface RunsFilter {
  customerId?: string;
  status?: string;
  routineSlug?: string; // Frontend-Filter (clientseitig)
  startedAfter?: string;
  limit?: number;
}

function runsFilterToParams(f: RunsFilter): Record<string, string | number | undefined> {
  return {
    customer_id: f.customerId,
    status: f.status,
    limit: f.limit ?? 50,
  };
}

export function useRuns(filter: RunsFilter = {}): ReturnType<
  typeof useQuery<RunListResponse>
> {
  return useQuery<RunListResponse>({
    queryKey: runKeys.list(filter),
    queryFn: () => api.get<RunListResponse>('/runs', runsFilterToParams(filter)),
    refetchInterval: (query) => {
      const data = query.state.data;
      const runs: Run[] = data?.data ?? [];
      const hasRunning = runs.some((r) => isActive(r.status));
      return hasRunning ? 5_000 : false;
    },
    staleTime: 5_000,
  });
}

export function useRun(id: string | undefined): ReturnType<typeof useQuery<RunDetail>> {
  return useQuery<RunDetail>({
    queryKey: runKeys.detail(id ?? ''),
    queryFn: () => api.get<RunDetail>(`/runs/${id ?? ''}`),
    enabled: Boolean(id),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 5_000;
      return isTerminal(data.status) ? false : 5_000;
    },
    staleTime: 2_000,
  });
}

export function useRunSteps(
  id: string | undefined,
  options: { enabled?: boolean } = {}
): ReturnType<typeof useQuery<RunStepsResponse>> {
  const enabled = Boolean(id) && options.enabled !== false;
  return useQuery<RunStepsResponse>({
    queryKey: runKeys.steps(id ?? ''),
    queryFn: () => api.get<RunStepsResponse>(`/runs/${id ?? ''}/steps`),
    enabled,
    refetchInterval: 5_000,
    staleTime: 2_000,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface StartRunInput {
  /** Routine via slug (Backend Feldname: routine_slug). */
  routineSlug: string;
  systemIds: string[];
  /** check=true => Dry-Run, check=false => Echtlauf. */
  checkMode: boolean;
  snapshot: boolean;
  /** ALTPFAD: Verzicht auf den geforderten Snapshot OHNE Begründung. Neue
   *  Aufrufer schicken stattdessen snapshotWaiverReason; bei destruktiven
   *  Routinen wirkt der Altpfad gar nicht. */
  snapshotOverride?: boolean;
  /** Begründeter Snapshot-Verzicht (Migration 0029): Regeln siehe
   *  components/runs/snapshotWaiver.ts - nur zusammen mit snapshot=false. */
  snapshotWaiverReason?: string;
  /** Benannte Ersatzsicherung; bei is_destructive Pflicht zum Verzicht. */
  snapshotWaiverEvidence?: string;
  idempotencyKey?: string;
}

interface StartRunBody {
  routine_slug: string;
  system_ids: string[];
  snapshot: boolean;
  snapshot_override?: boolean;
  snapshot_waiver_reason?: string;
  snapshot_waiver_evidence?: string;
  check_mode: boolean;
  idempotency_key?: string;
}

export interface StartRunResponse {
  run_id: string;
  status: string;
  mode: string;
  created: boolean;
  snapshot_ids: string[];
  /** Gewohnheitsbremse: ab dem dritten Verzicht desselben Kunden in 90
   *  Tagen. Der Lauf startet trotzdem - der Text MUSS aber sichtbar werden,
   *  sonst liest die Warnung wieder niemand. */
  snapshot_waiver_warning?: string | null;
}

export function useStartRun(): ReturnType<
  typeof useMutation<StartRunResponse, Error, StartRunInput>
> {
  const qc = useQueryClient();
  return useMutation<StartRunResponse, Error, StartRunInput>({
    mutationFn: ({
      routineSlug,
      systemIds,
      checkMode,
      snapshot,
      snapshotOverride,
      snapshotWaiverReason,
      snapshotWaiverEvidence,
      idempotencyKey,
    }) => {
      const body: StartRunBody = {
        routine_slug: routineSlug,
        system_ids: systemIds,
        check_mode: checkMode,
        snapshot,
      };
      if (snapshotOverride) body.snapshot_override = true;
      if (snapshotWaiverReason) body.snapshot_waiver_reason = snapshotWaiverReason;
      if (snapshotWaiverEvidence) body.snapshot_waiver_evidence = snapshotWaiverEvidence;
      if (idempotencyKey) body.idempotency_key = idempotencyKey;
      return api.post<StartRunResponse, StartRunBody>('/runs', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: runKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Komplettwartung pro Kunde: Vorschau + Batch-Start
// ---------------------------------------------------------------------------

/**
 * Ein Run trägt genau ein Playbook und Systeme genau eines Kunden
 * (RunEngine.start_run erzwingt beides). "Kunde komplett warten" ist deshalb
 * ein Run PRO ROUTINE - die Vorschau gruppiert die Systeme des Kunden nach
 * der Routine, die auf sie passt, und /runs/batch legt je Gruppe einen Lauf
 * an. Der Batch-Endpunkt ist bewusst teil-tolerant: einzelne Gruppen dürfen
 * scheitern, ohne die übrigen Läufe zu verhindern (=> errors[]).
 *
 * Beide Endpunkte stehen inzwischen in openapi.yaml/api.gen.ts
 * (CustomerMaintenancePreview, RunBatchResponse). Die Types bleiben hier
 * dennoch lokal: sie sind deckungsgleich, und der Umbau auf die generierten
 * Unions (status) gehört nicht in denselben Schritt wie die Spec-Nachpflege.
 */

export interface MaintenancePreviewRoutine {
  code: string;
  display_name: string;
  requires_snapshot: boolean;
  is_destructive: boolean;
  /** Reifegrad: experimental | beta | production | deprecated (siehe useRoutines). */
  tier: string;
  /** false = sichtbar, aber nicht vorausgewählt (bewusste Entscheidung). */
  auto_select?: boolean;
}

export interface MaintenancePreviewSystem {
  id: string;
  hostname: string;
  display_name?: string | null;
  // Letzte GEMELDETE Zugangsprüfung des wt-clients (Sprint 8, 13.08.2026).
  // Der Server misst nichts selbst (ADR-011) - access_checked_at ist seine
  // Empfangszeit. access_ok ist dreiwertig: null = noch nie geprüft.
  access_checked_at?: string | null;
  access_ok?: boolean | null;
  /** 'ok' | 'auth_denied' | 'no_route' | 'yubikey' | 'hostkey' | 'unknown'. */
  access_code?: string | null;
  access_detail?: string | null;
}

export interface MaintenancePreviewGroup {
  routine: MaintenancePreviewRoutine;
  systems: MaintenancePreviewSystem[];
}

/** System des Kunden, das in KEINE Gruppe fällt - mit Grund (Code-String). */
export interface MaintenancePreviewExclusion {
  system_id: string;
  hostname: string;
  reason: string;
}

export interface CustomerMaintenancePreview {
  customer: { id: string; name: string };
  groups: MaintenancePreviewGroup[];
  excluded: MaintenancePreviewExclusion[];
}

export function useCustomerMaintenancePreview(
  customerId: string | undefined
): ReturnType<typeof useQuery<CustomerMaintenancePreview>> {
  return useQuery<CustomerMaintenancePreview>({
    queryKey: runKeys.maintenancePreview(customerId ?? ''),
    queryFn: () =>
      api.get<CustomerMaintenancePreview>(
        `/customers/${customerId ?? ''}/maintenance-preview`
      ),
    enabled: Boolean(customerId),
    staleTime: 30_000,
  });
}

export interface StartBatchRunsInput {
  customerId: string;
  /** Je Eintrag ein Lauf. Leere Einträge filtert der Aufrufer vorher raus. */
  items: { routineSlug: string; systemIds: string[] }[];
  checkMode: boolean;
  snapshot: boolean;
  snapshotOverride?: boolean;
  /** Begründeter Snapshot-Verzicht, EIN Wert für alle Einträge (der Grund
   *  liegt in der Kundenumgebung, nicht in der einzelnen Routine). */
  snapshotWaiverReason?: string;
  snapshotWaiverEvidence?: string;
  /** Einmal pro Vorgang erzeugt - schützt den ganzen Batch vor Doppelklick. */
  idempotencyKey?: string;
}

interface BatchRunsBody {
  customer_id: string;
  items: { routine_slug: string; system_ids: string[] }[];
  check_mode: boolean;
  snapshot: boolean;
  snapshot_override?: boolean;
  snapshot_waiver_reason?: string;
  snapshot_waiver_evidence?: string;
  idempotency_key?: string;
}

export interface BatchRunResult {
  routine_slug: string;
  run_id: string;
  status: string;
  mode: string;
  /** false = Idempotency-Treffer, der Lauf existierte schon. */
  created: boolean;
  snapshot_ids: string[];
  /** Gewohnheitsbremse wie in StartRunResponse; steht an jedem Lauf, den der
   *  Verzicht tatsächlich betroffen hat. */
  snapshot_waiver_warning?: string | null;
}

export interface BatchRunError {
  routine_slug: string;
  message: string;
}

export interface BatchRunsResponse {
  runs: BatchRunResult[];
  errors: BatchRunError[];
}

export function useStartBatchRuns(): ReturnType<
  typeof useMutation<BatchRunsResponse, Error, StartBatchRunsInput>
> {
  const qc = useQueryClient();
  return useMutation<BatchRunsResponse, Error, StartBatchRunsInput>({
    mutationFn: ({
      customerId,
      items,
      checkMode,
      snapshot,
      snapshotOverride,
      snapshotWaiverReason,
      snapshotWaiverEvidence,
      idempotencyKey,
    }) => {
      const body: BatchRunsBody = {
        customer_id: customerId,
        items: items.map((i) => ({ routine_slug: i.routineSlug, system_ids: i.systemIds })),
        check_mode: checkMode,
        snapshot,
      };
      if (snapshotOverride) body.snapshot_override = true;
      if (snapshotWaiverReason) body.snapshot_waiver_reason = snapshotWaiverReason;
      if (snapshotWaiverEvidence) body.snapshot_waiver_evidence = snapshotWaiverEvidence;
      if (idempotencyKey) body.idempotency_key = idempotencyKey;
      return api.post<BatchRunsResponse, BatchRunsBody>('/runs/batch', body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: runKeys.all });
    },
  });
}

export interface ConfirmRunInput {
  /** ID des angesehenen Dry-Runs (mode=check). */
  dryRunId: string;
  /** Snapshot zusätzlich erzwingen (kann Routine-Pflicht nie unterbieten). */
  snapshot?: boolean;
}

// Aus dem generierten RunConfirm-Schema abgeleitet (bisher handgetippt):
// Partial, weil der Server für alles außer confirm Defaults hat und der
// Dialog nur sendet, was der Nutzer entschieden hat.
type ConfirmRunBody = Partial<RunConfirm> & { confirm: true };

/**
 * Leitet aus einem Dry-Run den Echtlauf ab (POST /runs/{dry_run_id}/confirm).
 * Routine + Zielsysteme kommen serverseitig aus dem Dry-Run - der Client muss
 * sie nicht erneut senden. Idempotent über Key `confirm:<dry_run_id>`.
 */
export function useConfirmRun(): ReturnType<
  typeof useMutation<StartRunResponse, Error, ConfirmRunInput>
> {
  const qc = useQueryClient();
  return useMutation<StartRunResponse, Error, ConfirmRunInput>({
    mutationFn: ({ dryRunId, snapshot }) => {
      const body: ConfirmRunBody = { confirm: true };
      if (snapshot !== undefined) body.snapshot = snapshot;
      return api.post<StartRunResponse, ConfirmRunBody>(
        `/runs/${dryRunId}/confirm`,
        body
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: runKeys.all });
    },
  });
}

export function useAbortRun(): ReturnType<typeof useMutation<Run, Error, string>> {
  const qc = useQueryClient();
  return useMutation<Run, Error, string>({
    mutationFn: (id) => api.post<Run>(`/runs/${id}/abort`, { reason: 'user-abort' }),
    onSuccess: (run) => {
      void qc.invalidateQueries({ queryKey: runKeys.detail(run.id) });
      void qc.invalidateQueries({ queryKey: runKeys.all });
    },
  });
}

export interface RollbackInput {
  runId: string;
  snapshotId?: string;
}

export interface RollbackResponse {
  run_id: string;
  snapshot_id: string;
  status: string;
  duration_ms: number;
}

export function useRollbackRun(): ReturnType<
  typeof useMutation<RollbackResponse, Error, RollbackInput>
> {
  const qc = useQueryClient();
  return useMutation<RollbackResponse, Error, RollbackInput>({
    mutationFn: ({ runId, snapshotId }) =>
      api.post<RollbackResponse>(`/runs/${runId}/rollback`, {
        confirm: true,
        ...(snapshotId ? { snapshot_id: snapshotId } : {}),
      }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: runKeys.detail(res.run_id) });
      void qc.invalidateQueries({ queryKey: runKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Snapshots & Diff
// ---------------------------------------------------------------------------

export interface Snapshot {
  id: string;
  run_id: string;
  system_id: string;
  customer_id: string;
  pve_node: string;
  pve_vmid: number;
  pve_snapshot_name: string;
  /** State: requested|creating|created|failed|restoring|restored|expired. */
  state: string;
  requested_at: string;
  created_at_pve?: string | null;
  restored_at?: string | null;
  expires_at?: string | null;
  error_message?: string | null;
}

export interface SnapshotListResponse {
  data: Snapshot[];
  total: number;
}

export function useRunSnapshots(
  runId: string | undefined,
  options: { enabled?: boolean } = {}
): ReturnType<typeof useQuery<SnapshotListResponse>> {
  const enabled = Boolean(runId) && options.enabled !== false;
  return useQuery<SnapshotListResponse>({
    queryKey: ['runs', 'detail', runId ?? '', 'snapshots'],
    queryFn: () =>
      // Backend erwartet die Filter-Alias-Syntax (routes/snapshots.py:
      // Query(alias="filter[run_id]")). Ein blankes `run_id` wird ignoriert
      // und lieferte Snapshots ALLER Runs.
      api.get<SnapshotListResponse>('/snapshots', { 'filter[run_id]': runId ?? '' }),
    enabled,
    staleTime: 30_000,
  });
}

// Diff-Aggregat ist auf der Frontend-Seite via Step-Stdout aggregiert.
// Backend liefert in v1 keinen separaten /runs/{id}/diff-Endpoint, deshalb
// rechnen wir lokal: changed-Steps + stdout_excerpt -> Heuristik. Dieser
// Type bleibt aber stabil falls das Backend in Sprint 6 einen Endpoint
// nachliefert.

export interface DiffSummaryEntry {
  kind: string;
  target: string;
  before?: string | null;
  after?: string | null;
  action?: string | null;
}

/**
 * Ein Eintrag aus diff.other: der vom wt-client geerntete Result-Block einer
 * Routine (wartungstool_result), ein Eintrag je Host. `result` bleibt bewusst
 * ein freies Dict - jede Routine druckt ihre eigenen Schlüssel, und die UI
 * zeigt sie roh als Schluessel/Wert-Liste statt sie zu deuten.
 */
export interface RoutineResultEntry {
  routine?: string | null;
  host?: string | null;
  result: Record<string, unknown>;
}

export interface SystemDiff {
  system_id: string;
  system_hostname: string;
  customer_name?: string | null;
  changed_files: DiffSummaryEntry[];
  changed_packages: DiffSummaryEntry[];
  changed_services: DiffSummaryEntry[];
  /**
   * Result-Blöcke außerhalb der drei Hauptkategorien. Zählt zum Diff:
   * ein Client-Lauf liefert oft NUR diesen Block, und "Kein Diff verfuegbar"
   * war dann schlicht falsch (Befund der Wartungsnächte 19./20.08.2026).
   */
  other?: RoutineResultEntry[];
  notes?: string | null;
}

export interface RunDiffResponse {
  run_id: string;
  generated_at: string;
  systems: SystemDiff[];
  truncated: boolean;
}
